import React, { useMemo, useRef, useEffect } from "react";
import * as d3 from "d3";

const TODAY = new Date();
TODAY.setUTCHours(0, 0, 0, 0);

function toDelta(c, unit) { return unit === "F" ? c * 9 / 5 : c; }

export function RecentAnomalyChart({ dailyMetricsData, unit }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);

  const { days, countAbove, avgAnomaly } = useMemo(() => {
    if (!dailyMetricsData) return { days: [], countAbove: 0, avgAnomaly: 0 };

    const entries = Object.entries(dailyMetricsData)
      .map(([ds, v]) => ({ date: new Date(ds + "T00:00:00Z"), ...v }))
      .filter(e => e.actual != null && e.actual_anomaly != null && e.date <= TODAY)
      .sort((a, b) => a.date - b.date)
      .slice(-100);

    const above = entries.filter(e => e.actual_anomaly > 0).length;
    const avg = entries.length
      ? entries.reduce((s, e) => s + e.actual_anomaly, 0) / entries.length
      : 0;

    return { days: entries, countAbove: above, avgAnomaly: avg };
  }, [dailyMetricsData]);

  const displayAvg = toDelta(avgAnomaly, unit);
  const isNetHot = avgAnomaly >= 0;

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || !days.length) return;

    const totalWidth = containerRef.current.getBoundingClientRect().width || 800;
    const margin = { top: 16, right: 16, bottom: 28, left: 44 };
    const width  = totalWidth - margin.left - margin.right;
    const height = 280;

    const anomalies = days.map(d => toDelta(d.actual_anomaly, unit));
    const yMin = Math.min(...anomalies);
    const yMax = Math.max(...anomalies);

    const xScale = d3.scaleBand()
      .domain(days.map((_, i) => i))
      .range([0, width])
      .padding(0.12);

    const yScale = d3.scaleLinear()
      .domain([yMin - 0.4, yMax + 0.4])
      .range([height, 0])
      .nice();

    const zero = yScale(0);

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg
      .attr("width", totalWidth)
      .attr("height", height + margin.top + margin.bottom);

    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Subtle grid lines
    g.selectAll(".grid-line")
      .data(yScale.ticks(5))
      .enter().append("line")
      .attr("class", "grid-line")
      .attr("x1", 0).attr("x2", width)
      .attr("y1", d => yScale(d)).attr("y2", d => yScale(d))
      .attr("stroke", "#2e2b27")
      .attr("stroke-width", 1);

    // Zero line (stronger)
    g.append("line")
      .attr("x1", 0).attr("x2", width)
      .attr("y1", zero).attr("y2", zero)
      .attr("stroke", "#3a3530")
      .attr("stroke-width", 1.5);

    // Bars
    g.selectAll(".abar")
      .data(days)
      .enter().append("rect")
      .attr("class", "abar")
      .attr("x", (_, i) => xScale(i))
      .attr("width", xScale.bandwidth())
      .attr("y", (_, i) => anomalies[i] >= 0 ? yScale(anomalies[i]) : zero)
      .attr("height", (_, i) => Math.max(Math.abs(yScale(anomalies[i]) - zero), 1))
      .attr("fill", d => d.actual_anomaly >= 0 ? "#ff4d1c" : "#4a7fa5")
      .attr("rx", 1)
      .style("opacity", 0.82);

    // Y axis
    g.append("g")
      .call(
        d3.axisLeft(yScale)
          .ticks(5)
          .tickFormat(d => `${d > 0 ? "+" : ""}${d.toFixed(1)}°`)
      )
      .call(sel => {
        sel.select(".domain").remove();
        sel.selectAll(".tick line").attr("stroke", "#3a3530");
        sel.selectAll(".tick text")
          .attr("fill", "#a89880")
          .style("font-size", "10px")
          .style("font-family", "Helvetica Neue, Arial, sans-serif");
      });

    // X axis: label at start of each month + the first day
    const monthStarts = days.reduce((acc, d, i) => {
      if (i === 0 || d.date.getUTCDate() === 1) acc.push({ i, date: d.date });
      return acc;
    }, []);

    g.selectAll(".month-label")
      .data(monthStarts)
      .enter().append("text")
      .attr("x", d => xScale(d.i) + xScale.bandwidth() / 2)
      .attr("y", height + 18)
      .attr("text-anchor", "middle")
      .attr("fill", "#5a5450")
      .style("font-size", "10px")
      .style("font-family", "Helvetica Neue, Arial, sans-serif")
      .text(d => d3.utcFormat("%-b %-d")(d.date));

  }, [days, unit]);

  if (!days.length) return null;

  return (
    <section className="recent-anomaly-section" ref={containerRef}>
      <div className="ra-card">
        <div className="ra-card-eyebrow">In the last {days.length} days…</div>
        <div className={`ra-card-count ${isNetHot ? "temp-hot" : "temp-cool"}`}>
          {countAbove} days
        </div>
        <div className="ra-card-sub">
          were warmer than the 1951–80 historical average,
          by an average of{" "}
          <span className={isNetHot ? "temp-hot" : "temp-cool"}>
            {isNetHot ? "+" : ""}{displayAvg.toFixed(1)}°{unit}
          </span>
        </div>
      </div>
      <svg ref={svgRef} style={{ width: "100%", display: "block" }} />
    </section>
  );
}
